/**
 * Constants for event names used in analytics tracking.
 *
 * These constants ensure consistency in event naming across the application.
 */

// ─── Canonical composed event names (ENG-4281 / ENG-4340 typed-name contract) ───
//
// Canonical names are COMPOSED from typed parts (`${root}_${status}`) instead of
// hand-written literals so an invalid root/status pair fails `tsc`, not a PostHog
// dashboard three weeks later. The matrix below is the per-root allowlist: the
// sanctioned special status `found` is only legal on its declared root
// (`discovery_device`), per the ENG-4340 map's token_set_note. Rename waves for
// other subsystems extend EVENT_ROOT / EVENT_STATUS / EVENT_STATUS_MATRIX with
// their own families — this ships the stage-6 discovery family (ENG-4312), the
// stage-4 bridge-link family (ENG-4311), the auth-login family (ENG-4315),
// the stage-1 installer root (ENG-4310), and the four standalone-web journey
// families (boot, studio_load, login, device_media_*)
// from ENG-4330. Every composed value is byte-identical to the literal it
// replaced, so no wire name and no saved PostHog asset changes.
// Per rule: reference-rules-in-comments (ai.knowledge/global); contract source:
// canonical-event-map.json `typed_name_implementation_contract` (ENG-4340).

export const EVENT_ROOT = {
  AUTH_LOGIN: 'auth_login',
  DISCOVERY: 'discovery',
  DISCOVERY_DEVICE: 'discovery_device',
  // Auto-linking the desktop app to its Bridge (ENG-4311, plan R-bridge).
  BRIDGE_LINK: 'bridge_link',
  // The Bridge's runtime reachability, distinct from the one-shot link attempt:
  // a linked bridge can still drop offline later, and that is a different funnel.
  BRIDGE_CONNECTION: 'bridge_connection',
  INSTALLER: 'installer',
  // The four standalone-web journey families (ENG-4330). These roots compose the
  // funnel stages the browser session reports, and are shared with desktop —
  // the surface is carried by the `client` property, never by a separate name.
  BOOT: 'boot',
  DEVICE_MEDIA_CONNECT: 'device_media_connect',
  DEVICE_MEDIA_STREAMING: 'device_media_streaming',
  // A desktop-only feature reached from the browser (ENG-4332). Its own root
  // because the product's answer there is guidance, not a stage outcome.
  FEATURE_GATE: 'feature_gate',
  LOGIN: 'login',
  STUDIO_LOAD: 'studio_load',
  // ENG-4321 terminal-outcome family: the bounded-window terminal outcome of a
  // retry-prone stream-recovery flow (see analytics/domains/terminal-outcome.ts).
  STREAM_RECOVERY: 'stream_recovery',
  // ENG-4305 (plan ONB): the onboarding wizard's own funnel. Two roots — the
  // wizard as a whole, and one step inside it — so a step event never has to
  // encode the step in its NAME (it rides the onboarding_step property instead).
  ONBOARDING: 'onboarding',
  ONBOARDING_STEP: 'onboarding_step'
} as const;

export type EventRoot = (typeof EVENT_ROOT)[keyof typeof EVENT_ROOT];

export const EVENT_STATUS = {
  EMPTY: 'empty',
  FOUND: 'found',
  ATTEMPT: 'attempt',
  GUIDANCE_SHOWN: 'guidance_shown',
  // `succeeded` and `success` are BOTH live tokens and are not interchangeable:
  // the ENG-4340 convention standardised on `_succeeded`, but the ENG-3495
  // media family shipped on `_success` and its PostHog assets still key on that
  // exact string. The matrix below is what stops a call site picking the wrong
  // one — each root sanctions only the token its wire name actually uses.
  SUCCEEDED: 'succeeded',
  SUCCESS: 'success',
  FAILED: 'failed',
  ERROR: 'error',
  STARTED: 'started',
  TIMEOUT: 'timeout',
  RETRY: 'retry',
  UNAVAILABLE: 'unavailable',
  // Sanctioned net-new status (ENG-4322 / ENG-4340 map §1 sanctioned-exceptions
  // list) — restricted to EVENT_ROOT.DISCOVERY, same as `found` is restricted
  // to EVENT_ROOT.DISCOVERY_DEVICE. Not a §2 in-funnel outcome token: it flags a
  // cross-protocol reconcile gap, not a stage pass/fail.
  MISMATCH: 'mismatch',
  SKIPPED: 'skipped',
  // A stage that failed terminally and then came good on its own. Netting the
  // earlier failure out needs its own status, or a recovered run reads as a drop.
  RECOVERED: 'recovered',
  VIEWED: 'viewed',
  SUBMITTED: 'submitted'
} as const;

export type EventStatus = (typeof EVENT_STATUS)[keyof typeof EVENT_STATUS];

// Root → the statuses that may legally compose with it. `satisfies` keeps the
// map total over EventRoot (adding a root without declaring its statuses is a
// compile error) without widening the per-root tuples.
export const EVENT_STATUS_MATRIX = {
  [EVENT_ROOT.AUTH_LOGIN]: [EVENT_STATUS.FAILED],
  [EVENT_ROOT.DISCOVERY]: [EVENT_STATUS.EMPTY, EVENT_STATUS.MISMATCH],
  [EVENT_ROOT.DISCOVERY_DEVICE]: [EVENT_STATUS.FOUND],
  [EVENT_ROOT.BOOT]: [EVENT_STATUS.STARTED, EVENT_STATUS.SUCCEEDED, EVENT_STATUS.FAILED],
  // The media families sanction `success`, not `succeeded` — the ENG-3495 wire
  // names shipped on that token and the saved PostHog assets key on it.
  [EVENT_ROOT.DEVICE_MEDIA_CONNECT]: [EVENT_STATUS.SUCCESS, EVENT_STATUS.ERROR],
  [EVENT_ROOT.DEVICE_MEDIA_STREAMING]: [EVENT_STATUS.SUCCESS, EVENT_STATUS.ERROR],
  // `guidance_shown` is sanctioned ONLY on the feature_gate root: a gate is the
  // one place where the product's answer to the user is guidance rather than an
  // outcome, so composing it onto an outcome root (discovery_guidance_shown)
  // must stay a compile error.
  [EVENT_ROOT.FEATURE_GATE]: [EVENT_STATUS.GUIDANCE_SHOWN],
  [EVENT_ROOT.LOGIN]: [EVENT_STATUS.SUCCEEDED],
  [EVENT_ROOT.STUDIO_LOAD]: [EVENT_STATUS.SUCCEEDED],
  // `empty` on bridge_link is the "no devices found yet" outcome — an EXPECTED
  // early-journey state (S3/warn), deliberately sharing the token the discovery
  // family already uses for the same meaning.
  [EVENT_ROOT.BRIDGE_LINK]: [
    EVENT_STATUS.ATTEMPT,
    EVENT_STATUS.SUCCEEDED,
    EVENT_STATUS.FAILED,
    EVENT_STATUS.TIMEOUT,
    EVENT_STATUS.EMPTY,
    EVENT_STATUS.RETRY
  ],
  [EVENT_ROOT.BRIDGE_CONNECTION]: [EVENT_STATUS.UNAVAILABLE],
  // The installer root has exactly one terminal: there is no success half. A
  // completed install is already observable as the next stage's boot events, so
  // sanctioning `installer_succeeded` would invite a duplicate of a signal we
  // already have.
  [EVENT_ROOT.INSTALLER]: [EVENT_STATUS.FAILED],
  // ENG-4321: only succeeded/failed are legal terminal statuses for the
  // stream-recovery root — composing it with `found`/`empty` fails type-checking.
  [EVENT_ROOT.STREAM_RECOVERY]: [EVENT_STATUS.SUCCEEDED, EVENT_STATUS.FAILED],
  // The wizard as a whole reaches a terminal outcome (succeeded / skipped /
  // failed / recovered); `viewed` and `submitted` describe a STEP, never the
  // whole wizard, so they are sanctioned only on the ONBOARDING_STEP root.
  [EVENT_ROOT.ONBOARDING]: [
    EVENT_STATUS.STARTED,
    EVENT_STATUS.SUCCEEDED,
    EVENT_STATUS.SKIPPED,
    EVENT_STATUS.FAILED,
    EVENT_STATUS.RECOVERED
  ],
  [EVENT_ROOT.ONBOARDING_STEP]: [EVENT_STATUS.VIEWED, EVENT_STATUS.STARTED, EVENT_STATUS.SUBMITTED]
} as const satisfies Record<EventRoot, readonly EventStatus[]>;

/** The statuses the matrix sanctions for a given root. */
export type StatusFor<R extends EventRoot> = (typeof EVENT_STATUS_MATRIX)[R][number];

/**
 * Compose a canonical event name from typed parts. The return type is the
 * template literal `${R}_${S}`, so call sites keep literal-typed constants and
 * an off-matrix (root, status) pair is rejected by the StatusFor<R> constraint
 * at compile time — the ENG-4340 contract's "invalid combinations fail
 * type-checking" requirement.
 */
export const eventName = <R extends EventRoot, S extends StatusFor<R>>(
  root: R,
  status: S
): `${R}_${S}` => `${root}_${status}`;

export const EVENT_NAMES = {
  ADD_AUDIO_SOURCE: 'add_audio_source',
  ADD_GAMEPAD_CONTROLLER: 'add_gamepad_controller',
  ADD_PAYMENT_METHOD: 'add_payment_method',
  ADD_POSITION_PRESET: 'add_position_preset',
  RECALL_POSITION_PRESET: 'recall_position_preset',
  ADD_PRESET_GROUP: 'add_preset_group',
  AUTO_TRACKING_LOSE_SUBJECT: 'lose_subject',
  AUTO_TRACKING_SELECT_SUBJECT: 'select_subject',
  AUTO_TRACKING_START: 'start_tracking',
  AUTO_TRACKING_STOP: 'stop_tracking',
  AUTO_TRACKING_STARTED: 'auto_tracking_started',
  AUTO_TRACKING_STOPPED: 'auto_tracking_stopped',
  // ENG-3960 §E12: distinct from AUTO_TRACKING_STARTED (a tracking session begins) —
  // ACTIVATED marks the user first enabling the feature. Pre-declared for the wave.
  AUTO_TRACKING_ACTIVATED: 'auto_tracking_activated',
  AUTO_TRACKING_SUBJECT_ACQUIRED: 'auto_tracking_subject_acquired',
  AUTO_TRACKING_SUBJECT_LOST: 'auto_tracking_subject_lost',
  BRIDGE_GOES_OFFLINE: 'bridge_goes_offline',
  // ENG-4052 account-first bootstrap resolution receipt (branch-side).
  BRIDGE_BOOTSTRAP_RESOLVED: 'bridge_bootstrap_resolved',
  // Canonical rename target for BRIDGE_GOES_OFFLINE (ENG-4311, plan R-bridge).
  // The historical name stays above for compatibility readers, not emission.
  BRIDGE_CONNECTION_UNAVAILABLE: eventName(EVENT_ROOT.BRIDGE_CONNECTION, EVENT_STATUS.UNAVAILABLE),
  CALIBRATE_SOURCE: 'calibrate_source',
  // Canonical terminal calibration event (ENG-4026). Emitted from the
  // <Calibration> watchdog on the first transition out of RUNNING.
  CALIBRATION_COMPLETED: 'calibration_completed',
  CANCEL_CALIBRATION: 'cancel_calibration',
  CHANGE_AUDIO_REFERENCE_SOURCE: 'change_audio_reference_source',
  CHANGE_FOCUS_SPEED: 'change_focus_speed',
  CHANGE_HOTKEY: 'change_hotkey',
  CHANGE_PASSWORD: 'change_password',
  CLICK_AUTO_TRACKING: 'click_auto_tracking',
  CLICK_CENTER_TOOL: 'click_center_tool',
  CLICK_CINE_CENTER: 'click_cine_center',
  CLICK_CINE_FRAME: 'click_cine_frame',
  CLICK_CURSOR_TOOL: 'click_cursor_tool',
  CLICK_DOWNLOAD_INVOICE: 'click_download_invoice',
  CLICK_EXPOSURE_SETTINGS: 'click_exposure_settings',
  CLICK_FAST_FRAME: 'click_fast_frame',
  CLICK_FEATURE_SETTINGS: 'click_feature_settings',
  CLICK_FOCUS: 'click_focus',
  CLICK_FOCUS_SETTINGS: 'click_focus_settings',
  CLICK_GENERAL_SETTINGS: 'click_general_settings',
  CLICK_IMAGE_SETTING_PRESETS: 'click_image_setting_presets',
  CLICK_NETWORK_SETTINGS: 'click_network_settings',
  CLICK_NOTIFICATIONS_TRAY: 'click_notifications_tray',
  CLICK_QUICK_SHOT: 'click_quick_shot',
  CLICK_QUICK_SHOTS: 'click_quick_shots',
  CLICK_TO_CENTER_SHOT: 'click_to_center_shot',
  CLICK_UPGRADE_PLAN: 'click_upgrade_plan',
  CLICK_STAY_ON_FREE: 'click_stay_on_free',
  CLICK_WHITE_BALANCE_SETTINGS: 'click_white_balance_settings',
  COLLAB_ACCEPT_INVITE: 'collab_accept_invite',
  COLLAB_CREATE_LINK: 'collab_create_link',
  COLLAB_INVITE_USER: 'collab_invite_user',
  COLLAB_OPEN_INVITE: 'collab_open_invite',
  COLLAB_REMOVE_USER: 'collab_remove_user',
  CONFIRM_CINE_FRAME_SHOT: 'confirm_cine_frame_shot',
  CONFIRM_FAST_FRAME_SHOT: 'confirm_fast_frame_shot',
  CONFIRM_LOCAL_VIDEO_SETTINGS: 'confirm_local_video_settings',
  CONFIRM_NDI_VIDEO_SETTINGS: 'confirm_ndi_video_settings',
  CONFIRM_RTSP_VIDEO_SETTINGS: 'confirm_rtsp_video_settings',
  // RTSP Assisted Setup (Multi-Add Phase 2 enrichment). Pre-declared per
  // the wave; emit sites in `video/components/add/video/rtsp/rtsp.tsx`.
  // Per `reference-rules-in-comments` (ai.knowledge/global).
  /** User manually edited the Stream URL (flipped the picker into manual mode). */
  RTSP_MANUAL_URL_EDITED: 'rtsp_manual_url_edited',
  /**
   * RTSP Check-Connectivity outcome resolved. `connectivityOutcome`
   * distinguishes auth-failed (credentials rejected) from connection-failed
   * (host unreachable / stream error) — today both collapse into a generic
   * connect error. NO credential-bearing URL is ever attached.
   */
  RTSP_CONNECTIVITY_OUTCOME: 'rtsp_connectivity_outcome',
  CREATE_NEW_ORGANIZATION: 'create_new_organization',
  CREATE_STUDIO: 'studio_create',
  DEVICE_CONTROLS_CONNECT_ERROR: 'device_controls_connect_error',
  DEVICE_CONTROLS_CONNECT_SUCCESS: 'device_controls_connect_success',
  DELETE_IMAGE_SETTING_PRESET: 'delete_image_setting_preset',
  DELETE_ORGANIZATION: 'delete_organization',
  DELETE_POSITION_PRESET: 'delete_position_preset',
  DELETE_STUDIO: 'delete_studio',
  DEVICE_ADD: 'device_add',
  // ENG-4323: net-new, SINGLE-EMIT failure terminal for the add-camera step (no
  // legacy dual-write). Pairs with the ENG-4304 `device_add_failed` PostHog
  // $exception Issue of the same name — the two join on session_id + device_id.
  DEVICE_ADD_FAILED: 'device_add_failed',
  DEVICE_AUTHENTICATE: 'device_authenticate',
  DEVICE_AUTH_INVALID: 'device_auth_invalid',
  // Claim HTTP succeeded and no device_shadowed arrived within
  // DEVICE_SHADOW_TIMEOUT_MS. Net-new, no legacy twin.
  DEVICE_SHADOW_TIMEOUT: 'device_shadow_timeout',
  DEVICE_CLICK_DISCOVERED: 'device_click_discovered',
  DEVICE_CLICK_MANUAL_ADD: 'device_click_manual_add',
  DEVICE_CONTINUE_FROM_BRAND: 'device_continue_from_brand',
  DEVICE_CONTINUE_FROM_CONTROL: 'device_continue_from_control',
  DEVICE_CONTINUE_FROM_VIDEO: 'device_continue_from_video',
  DEVICE_CONTINUE_TO_VIDEO: 'device_continue_to_video',
  // Additive (proposal §5b-1): fires when the operator hits Skip on a known-model
  // INVALID credential verdict. Closes One-Click gap G5 — the Skip action had no
  // event. UPPER_SNAKE key → snake_case wire value, device_ prefix, grouped
  // alphabetically among the DEVICE_* entries.
  DEVICE_CREDENTIALS_SKIPPED: 'device_credentials_skipped',
  DEVICE_DISCOVERY_DEVICE_FOUND: 'device_discovery_device_found',
  DEVICE_DISCOVERY_NO_DEVICES_FOUND: 'device_discovery_no_devices_found',
  // Canonical stage-6 discovery names (ENG-4312, per the ENG-4340 canonical map).
  // Composed via the typed builder above; dual-emitted alongside the legacy
  // DEVICE_DISCOVERY_* constants for the ≥30-day parity window (no hard cutover)
  // so no saved PostHog insight breaks. discovery_empty is S3/warn — "no cameras
  // found yet" is EXPECTED, not a failure, and must stay out of blocking-spike
  // alerts (see EVENT_SEVERITY in event-properties.ts).
  DISCOVERY_DEVICE_FOUND: eventName(EVENT_ROOT.DISCOVERY_DEVICE, EVENT_STATUS.FOUND),
  DISCOVERY_EMPTY: eventName(EVENT_ROOT.DISCOVERY, EVENT_STATUS.EMPTY),
  // Net-new stage-6 discovery event (ENG-4322): a physical device is reported
  // twice in the same pass under different protocols with different labels
  // (e.g. NDI vs ONVIF) — a reconcile gap that fires zero today. No legacy
  // twin/dual-emit: there is nothing to migrate away from.
  DISCOVERY_MISMATCH: eventName(EVENT_ROOT.DISCOVERY, EVENT_STATUS.MISMATCH),
  DEVICE_DELETE: 'device_delete',
  // ENG-3495 media family, composed from typed parts (ENG-4330). The `_success`
  // token — not `_succeeded` — is the shipped wire name these PostHog assets key
  // on; the matrix is what keeps the two spellings from being swapped by hand.
  DEVICE_MEDIA_CONNECT_ERROR: eventName(EVENT_ROOT.DEVICE_MEDIA_CONNECT, EVENT_STATUS.ERROR),
  DEVICE_MEDIA_CONNECT_SUCCESS: eventName(EVENT_ROOT.DEVICE_MEDIA_CONNECT, EVENT_STATUS.SUCCESS),
  DEVICE_MEDIA_QUALITY_PING: 'device_media_quality_ping',
  DEVICE_MEDIA_STREAMING_ERROR: eventName(EVENT_ROOT.DEVICE_MEDIA_STREAMING, EVENT_STATUS.ERROR),
  DEVICE_MEDIA_STREAMING_SUCCESS: eventName(
    EVENT_ROOT.DEVICE_MEDIA_STREAMING,
    EVENT_STATUS.SUCCESS
  ),
  // Canonical media/streaming/controls names (ENG-4313, per the ENG-4300/4340
  // canonical map). Active migrations emit both names; retired legacy constants remain
  // available for historical catalog metadata and compatibility readers.
  MEDIA_CONNECT_FAILED: 'media_connect_failed',
  MEDIA_CONNECT_SUCCEEDED: 'media_connect_succeeded',
  MEDIA_STREAM_FAILED: 'media_stream_failed',
  MEDIA_STREAM_SUCCEEDED: 'media_stream_succeeded',
  MEDIA_QUALITY_PING: 'media_quality_ping',
  CONTROLS_CONNECT_FAILED: 'controls_connect_failed',
  CONTROLS_CONNECT_SUCCEEDED: 'controls_connect_succeeded',
  DEVICE_MOVE_DIRECT: 'device_move_direct',
  DEVICE_MOVE_END: 'device_move_end',
  DEVICE_MOVE_START: 'device_move_start',
  DEVICE_RESTORE: 'device_restore',
  // ENG-3714 (stabilization): net-new observability terminal for the
  // restoreAgent dedupe skip branch, previously an unmeasurable console.log.
  // No dual_write block anywhere: nothing fired here before, so there is no
  // legacy twin to sunset. Deliberately NOT in DEVICE_EVENTS: the skip fires
  // before any catalog context is resolved, so it carries only
  // agent_id/device_id/skip_reason rather than the device-cohort props.
  DEVICE_RESTORE_SKIPPED: 'device_restore_skipped',
  DEVICE_SELECT_BRAND: 'device_select_brand',
  DEVICE_SELECT_MODEL: 'device_select_model',
  DEVICE_SETTINGS_CHANGE: 'device_settings_change',
  DEVICE_SHADOWED: 'device_shadowed',
  DOWNLOAD_APP: 'download_app',
  // ENG-4347 stage-1 rename: canonical successor of `download_electron_app`
  // (USER_DOWNLOAD_ELECTRON_APP). Both fire during the ≥30-day dual-write
  // window; retire the legacy name only after PostHog parity is verified.
  DOWNLOAD_APP_STARTED: 'download_app_started',
  // ENG-4310 stage-1 net-new failure terminal, composed via the typed builder
  // above. Net-new means NO dual-emit: nothing fires here today, so there is no
  // legacy twin to keep alive and nothing for a retirement ticket to sunset.
  // Sibling of DOWNLOAD_APP_STARTED in the same stage-1 download family — the
  // `installer` root (not `download`) because it marks the install never
  // happening, not the download never starting.
  INSTALLER_FAILED: eventName(EVENT_ROOT.INSTALLER, EVENT_STATUS.FAILED),
  DOWNGRADE_PLAN: 'downgrade_plan',
  ADVANCED_SETTINGS_OPENED: 'advanced_settings_opened',
  ADVANCED_SETTINGS_EXPORTED: 'advanced_settings_exported',
  // ENG-3960 §E12: pre-declared so feature waves wire the close half of the
  // advanced-settings funnel without re-introducing a duplicate constant.
  ADVANCED_SETTINGS_CLOSED: 'advanced_settings_closed',
  EDIT_ADVANCED_CONTROL_SETTINGS: 'edit_advanced_control_settings',
  EDIT_ADVANCED_VIDEO_SETTINGS: 'edit_advanced_video_settings',
  EDIT_CONTROL_SETTINGS: 'edit_control_settings',
  EDIT_SPEED_SETTINGS: 'edit_speed_settings',
  ENABLE_2FA: 'enable_2fa',
  DISABLE_2FA: 'disable_2fa',
  AUTO_SET_VARIABLE_SPEED_MAX: 'auto_set_variable_speed_max',
  ENTER_ONVIF_PASSWORD: 'enter_onvif_password',
  FAILED_CALIBRATION: 'failed_calibration',
  // ENG-4332 (plan G-web-guidance): a browser user reaching a desktop-only feature is a
  // normal platform limitation, so it is recorded as GUIDANCE rather than as an
  // `$exception` that pollutes error groups. Net-new name — nothing is renamed or
  // retired, so there is no dual-write leg. Composed via the typed builder above so an
  // off-matrix root/status pair cannot compile.
  FEATURE_GATE_GUIDANCE_SHOWN: eventName(EVENT_ROOT.FEATURE_GATE, EVENT_STATUS.GUIDANCE_SHOWN),
  LOADED_STUDIO: 'loaded_studio',
  // Canonical rename target for LOADED_STUDIO. The legacy constant remains above so
  // historical rows stay representable, but current senders emit only this name.
  STUDIO_LOAD_SUCCEEDED: eventName(EVENT_ROOT.STUDIO_LOAD, EVENT_STATUS.SUCCEEDED),
  LOCK_LOCAL_MODE: 'lock_local_mode',
  MOVE_PAN_TILT_JOYSTICK: 'move_pan_tilt_joystick',
  ORG_CREATE: 'org_create',
  ORG_DELETE: 'org_delete',
  ORG_UPLOAD_PHOTO: 'org_upload_photo',
  // ENG-4348 stage 3: canonical rename of OAUTH_ERROR. The name is shared ON
  // PURPOSE with the missing-refresh-token PostHog Issue — both describe the
  // same product failure ("the user could not log in"), so dashboards and
  // alerts key on one failure name regardless of which detector fired.
  // Dual-emitted alongside OAUTH_ERROR for >=30 days (see
  // AUTH_LOGIN_FAILURE_EVENT_NAMES below); no call site may emit either name
  // directly. ENG-4315 composed it from the typed parts above (value unchanged)
  // so the auth family gains the same off-matrix compile rejection the
  // discovery family already has.
  AUTH_LOGIN_FAILED: eventName(EVENT_ROOT.AUTH_LOGIN, EVENT_STATUS.FAILED),
  // Legacy name for AUTH_LOGIN_FAILED (ENG-4348 dual-write; see
  // canonical-event-map.json sunset_window_days before removing).
  OAUTH_ERROR: 'oauth_error',
  LOGIN_CONTINUE_WITH_EMAIL_CLICKED: 'login_continue_with_email_clicked',
  // ENG-3451: sibling of LOGIN_CONTINUE_WITH_EMAIL_CLICKED for the Electron
  // login screen's Google option. A distinct name (not a property on the
  // email event) so the two funnels never merge in PostHog.
  LOGIN_CONTINUE_WITH_GOOGLE_CLICKED: 'login_continue_with_google_clicked',
  LOGIN_CREATE_ACCOUNT_CLICKED: 'login_create_account_clicked',
  PERMISSIONS_MODAL_CONTINUE: 'permissions_modal_continue',
  PERMISSIONS_MODAL_CLOSED: 'permissions_modal_closed',
  PERMISSIONS_MODAL_ERROR: 'permissions_modal_error',
  PERMISSIONS_MODAL_OPENED: 'permissions_modal_opened',
  PERMISSIONS_MODAL_PERMISSION_ACTION: 'permissions_modal_permission_action',
  PERMISSIONS_MODAL_SKIP: 'permissions_modal_skip',
  // Canonical permissions grant terminals. The old error constant stays available for
  // historical compatibility, but new failure sends use GRANT_FAILED only.
  PERMISSIONS_GRANT_FAILED: 'permissions_grant_failed',
  PERMISSIONS_GRANT_SUCCEEDED: 'permissions_grant_succeeded',
  // ─── Onboarding wizard (ENG-4305, plan ONB) ────────────────────────────────
  // Canonical names, composed by the typed builder above so an off-matrix
  // (root, status) pair cannot be written here. The wizard's own second
  // telemetry framework emitted the legacy twins below via a raw PostHog
  // capture; the shared framework now owns every one of them. Which legacy name
  // pairs with which canonical name — and which legacy names are dropped as
  // duplicates of an existing canonical event — is declared in
  // constants/onboarding-events.ts (ONBOARDING_EVENT_OWNERSHIP).
  ONBOARDING_STARTED: eventName(EVENT_ROOT.ONBOARDING, EVENT_STATUS.STARTED),
  ONBOARDING_SUCCEEDED: eventName(EVENT_ROOT.ONBOARDING, EVENT_STATUS.SUCCEEDED),
  ONBOARDING_SKIPPED: eventName(EVENT_ROOT.ONBOARDING, EVENT_STATUS.SKIPPED),
  ONBOARDING_FAILED: eventName(EVENT_ROOT.ONBOARDING, EVENT_STATUS.FAILED),
  ONBOARDING_RECOVERED: eventName(EVENT_ROOT.ONBOARDING, EVENT_STATUS.RECOVERED),
  ONBOARDING_STEP_VIEWED: eventName(EVENT_ROOT.ONBOARDING_STEP, EVENT_STATUS.VIEWED),
  ONBOARDING_STEP_STARTED: eventName(EVENT_ROOT.ONBOARDING_STEP, EVENT_STATUS.STARTED),
  ONBOARDING_STEP_SUBMITTED: eventName(EVENT_ROOT.ONBOARDING_STEP, EVENT_STATUS.SUBMITTED),
  ONBOARDING_ADDRESS_RESOLVED: 'onboarding_address_resolved',
  // Legacy wizard names, dual-emitted beside their canonical twin through the
  // parity window (no hard cutover). New call sites must use the tracker in
  // analytics/domains/onboarding-lifecycle.ts, never these constants directly.
  ONBOARDING_FUNNEL_STARTED: 'onboarding_funnel_started',
  ONBOARDING_WELCOME_VIEWED: 'onboarding_welcome_viewed',
  ONBOARDING_BRIDGE_CONCEPT_VIEWED: 'onboarding_bridge_concept_viewed',
  ONBOARDING_BRIDGE_DETECT_STARTED: 'onboarding_bridge_detect_started',
  ONBOARDING_CHOOSE_METHOD_VIEWED: 'onboarding_choose_method_viewed',
  ONBOARDING_METHOD_CHOSEN: 'onboarding_method_chosen',
  ONBOARDING_DISCOVERY_STARTED: 'onboarding_discovery_started',
  ONBOARDING_CAMERA_FORM_VIEWED: 'onboarding_camera_form_viewed',
  ONBOARDING_CAMERA_SUBMITTED: 'onboarding_camera_submitted',
  ONBOARDING_CONNECTION_TEST_STARTED: 'onboarding_connection_test_started',
  ONBOARDING_MAGIC_MOMENT_REACHED: 'onboarding_magic_moment_reached',
  ONBOARDING_COMPLETE_REACHED: 'onboarding_complete_reached',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  ONBOARDING_BRIDGE_CONNECT_RECOVERED: 'onboarding_bridge_connect_recovered',
  ONBOARDING_ERROR: 'onboarding_error',
  PREVIOUSLY_AUTHENTICATED_USER_REACHED_LOGIN: 'previously_authenticated_user_reached_login',
  // Registration funnel events
  REGISTRATION_STARTED: 'registration_started',
  REGISTRATION_COMPLETED: 'registration_completed',
  // Email-verification gate funnel events (ENG-3797, spec §analytics).
  // PRIVACY: never attach the raw email to any of these.
  VERIFICATION_GATE_SHOWN: 'verification_gate_shown',
  VERIFICATION_EMAIL_SENT: 'verification_email_sent',
  VERIFICATION_RESENT: 'verification_resent',
  VERIFICATION_COMPLETED: 'verification_completed',
  VERIFICATION_POLL_FAILED: 'verification_poll_failed',
  VERIFICATION_STATUS_UNKNOWN: 'verification_status_unknown',
  // Fired once when the gate allows a NON-verified account only because the
  // server marked it grandfathered (verificationRequired === false). Lets
  // rollout watch the exempted legacy population (ENG-4602). Not fired on the
  // ordinary verified-allow path.
  VERIFICATION_GATE_GRANDFATHERED: 'verification_gate_grandfathered',
  AGENT_SETUP_STARTED: 'agent_setup_started',
  LOGIN_SCREEN_LOADED: 'login_screen_loaded',
  LOGIN_SUCCEEDED: eventName(EVENT_ROOT.LOGIN, EVENT_STATUS.SUCCEEDED),
  SIGNUP_PREQUALIFICATION_STARTED: 'signup_prequalification_started',
  SIGNUP_PREQUALIFICATION_COMPLETED: 'signup_prequalification_completed',
  OVERRIDES_BRIDGE: 'overrides_bridge',
  PRESS_ADD_IP_CONTROLLER: 'press_add_ip_controller',
  PRESS_SHARE_BUTTON: 'click_share_button',
  RENAME_BRIDGE: 'rename_bridge',
  RESEND_INVITE: 'resend_invite',
  RESET_ALL_IMAGE_SETTINGS: 'reset_all_image_settings',
  RESTART_BRIDGE: 'restart_bridge',
  SAVE_HOTKEY_CHANGES: 'save_hotkey_changes',
  SAVE_IMAGE_SETTING_PRESET: 'save_image_setting_preset',
  SELECT_ANNUAL_STANDARD_PLAN: 'select_annual_standard_plan',
  SELECT_CONTROL_TYPE: 'select_control_type',
  SELECT_DISCOVERY_SOURCE: 'select_discovery_source',
  SELECT_LOCAL_SOURCE: 'select_local_source',
  SELECT_NDI_SOURCE: 'select_ndi_source',
  SELECT_NO_CONTROL: 'select_no_control',
  SELECT_ONVIF_SOURCE: 'select_onvif_source',
  SELECT_PERMISSION_TYPE: 'select_permission_type',
  SELECT_PRO_PLAN: 'select_pro_plan',
  SELECT_RTSP_SOURCE: 'select_rtsp_source',
  SELECT_STANDARD_PLAN: 'select_standard_plan',
  SELECT_VIEW_TYPE: 'select_view_type',
  SENDS_BRIDGE_LINK_REQUEST: 'send_bridge_link_request',
  SET_AUDIO_DELAY: 'set_audio_delay',
  SET_NEW_HOTKEY: 'set_new_hotkey',
  SET_SOURCE_TO_SLEEP: 'set_source_to_sleep',
  SHARE_EXPIRING_LINK: 'share_expiring_link',
  SKIP_MODEL_ENTRY: 'skip_model_entry',
  START_DOWNLOAD_FLOW: 'start_download_flow',
  START_RECORDING: 'start_recording',
  SUCCESSFULLY_ADD_AUDIO_SOURCE: 'success_add_audio_source',
  SUCCESSFULLY_ADD_CAMERA: 'successfully_add_camera',
  SUCCESSFULLY_ADD_IP_CONTROLLER: 'successfully_add_ip_controller',
  SUCCESSFULLY_CALIBRATE_SOURCE: 'successfully_calibrate_source',
  SUCCESSFULLY_SUBSCRIBE_ANNUAL_PLAN: 'successfully_subscribe_annual_plan',
  SUCCESSFULLY_SUBSCRIBE_PAID_PLAN: 'successfully_subscribe_paid_plan',
  TIME_SPENT_IN_TOOL: 'time_spent_in_tool',
  TOGGLE_CLOUD_MODE: 'toggle_cloud_mode',
  TOGGLE_LOCAL_MODE: 'toggle_local_mode',
  TOGGLE_PTZ_GRID_LINES: 'toggle_ptz_gridlines',
  UNIVERSAL_LOGIN_PAGE_LOADED: 'universal_login_page_loaded',
  UNIVERSAL_LOGIN_SUBMITTED: 'universal_login_submitted',
  UNIVERSAL_SIGNUP_PAGE_LOADED: 'universal_signup_page_loaded',
  UNIVERSAL_SIGNUP_SUBMITTED: 'universal_signup_submitted',
  UNIVERSAL_SWITCH_TO_LOGIN_CLICKED: 'universal_switch_to_login_clicked',
  UNIVERSAL_SWITCH_TO_SIGNUP_CLICKED: 'universal_switch_to_signup_clicked',
  APP_BOOT_STARTED: 'app_boot_started',
  APP_BOOT_COMPLETED: 'app_boot_completed',
  APP_BOOT_FAILED: 'app_boot_failed',
  // Canonical stage-2 boot names. The APP_BOOT_* constants remain above so historical
  // rows stay representable, but current senders emit only these names. `_succeeded`
  // replaces `_completed` per the signed-off naming convention.
  BOOT_STARTED: eventName(EVENT_ROOT.BOOT, EVENT_STATUS.STARTED),
  BOOT_SUCCEEDED: eventName(EVENT_ROOT.BOOT, EVENT_STATUS.SUCCEEDED),
  BOOT_FAILED: eventName(EVENT_ROOT.BOOT, EVENT_STATUS.FAILED),
  // ENG-4052 account-first Studio bootstrap events (branch-side).
  ROOMS_FETCH_FAILED: 'rooms_fetch_failed',
  STUDIO_CREATED_SILENT: 'studio_created_silent',
  STUDIO_PICKER_OPENED: 'studio_picker_opened',
  STUDIO_SELECTED: 'studio_selected',
  STUDIO_PICKER_RENDER_FAILED: 'studio_picker_render_failed',
  STUDIO_LINK_FAILED: 'studio_link_failed',
  STUDIO_CREATE_REQUESTED: 'studio_create_requested',
  STUDIO_CREATED_FROM_PICKER: 'studio_created_from_picker',
  STUDIO_CREATE_FAILED: 'studio_create_failed',
  STUDIO_LINK_PERMISSION_DENIED: 'studio_link_permission_denied',
  STUDIO_LINK_SUCCEEDED: 'studio_link_succeeded',
  // Bridge Setup's completion terminal: the local Bridge is verified linked and its Studio opens.
  STUDIO_SETUP_SUCCEEDED: 'studio_setup_succeeded',
  STUDIO_MULTIBRIDGE_CONFIRMATION_OPENED: 'studio_multibridge_confirmation_opened',
  STUDIO_MULTIBRIDGE_CONFIRMATION_CONFIRMED: 'studio_multibridge_confirmation_confirmed',
  STUDIO_MULTIBRIDGE_CONFIRMATION_CANCELLED: 'studio_multibridge_confirmation_cancelled',
  STUDIO_REPLACEMENT_CONFIRMATION_OPENED: 'studio_replacement_confirmation_opened',
  STUDIO_REPLACEMENT_CONFIRMATION_CONFIRMED: 'studio_replacement_confirmation_confirmed',
  STUDIO_REPLACEMENT_CONFIRMATION_CANCELLED: 'studio_replacement_confirmation_cancelled',
  // ENG-4419: net-new canonical signal for a launch restart-loop (the main
  // process relaunching repeatedly without ever recovering). It has NO legacy
  // twin, so it is canonical-only with no dual-write — unlike the BOOT_* rename
  // trio above. Emitted from the Electron MAIN process (a relaunch loop may
  // never keep a renderer alive long enough to emit), so no renderer call site
  // references this constant; it lives here as the single source of truth the
  // canonical-event-map.json registry and the e2e receipt assert against.
  BOOT_RELAUNCH_LOOP: 'boot_relaunch_loop',
  // Spec 4 (studio startup latency): per-DOCUMENT renderer startup diagnostics.
  // Deliberately NOT part of the BOOT_* trio. Those are launch-scoped and claimed
  // once per Electron launch (routes/root/bootstrap-analytics.ts), so a refresh
  // emits none of them; these two are keyed by renderer_load_id and emit on EVERY
  // document, including a /room/:id refresh that never mounts the root bootstrap.
  // One RENDERER_STARTUP_MEASURED per document; N RENDERER_STARTUP_PHASE spans.
  RENDERER_STARTUP_PHASE: 'renderer_startup_phase',
  RENDERER_STARTUP_MEASURED: 'renderer_startup_measured',
  BRIDGE_UPDATE_STARTED: 'bridge_update_started',
  BRIDGE_UPDATE_COMPLETED: 'bridge_update_completed',
  BRIDGE_UPDATE_FAILED: 'bridge_update_failed',
  UPDATE_ORGANIZATION_INFO: 'update_organization_info',
  UPDATE_STUDIO_NAME: 'update_studio_name',
  UPLOAD_ORG_PROFILE_PICTURE: 'upload_org_profile_picture',
  USER_DELETE_ACCOUNT: 'delete_account',
  // ENG-4347 stage-1 rename: legacy alias of DOWNLOAD_APP_STARTED, kept for the
  // dual-write window only. New call sites must use DOWNLOAD_APP_STARTED.
  USER_DOWNLOAD_ELECTRON_APP: 'download_electron_app',
  USER_LINK_BRIDGE: 'link_bridge',
  USER_OPEN_BRIDGE_TRAY: 'open_bridge_tray',
  USER_OPEN_ELECTRON_TRAY: 'open_electron_tray',
  USER_OPEN_MONITOR_TRAY: 'open_monitor_tray',
  USER_UNLINK_BRIDGE: 'unlink_bridge',
  // Historical auto-link names stay exported so compatibility readers can identify
  // retained rows. The catalog and emission tuples decide which senders remain active.
  // DISABLED and MANUAL_FALLBACK were never part of the rename wave.
  AUTO_LINK_BRIDGE_ATTEMPT: 'auto_link_bridge_attempt',
  AUTO_LINK_BRIDGE_SUCCESS: 'auto_link_bridge_success',
  AUTO_LINK_BRIDGE_FAILURE: 'auto_link_bridge_failure',
  AUTO_LINK_BRIDGE_NO_DEVICES: 'auto_link_bridge_no_devices',
  AUTO_LINK_BRIDGE_ERROR: 'auto_link_bridge_error',
  AUTO_LINK_BRIDGE_RETRY: 'auto_link_bridge_retry',
  AUTO_LINK_BRIDGE_TIMEOUT: 'auto_link_bridge_timeout',
  AUTO_LINK_BRIDGE_DISABLED: 'auto_link_bridge_disabled',
  AUTO_LINK_BRIDGE_MANUAL_FALLBACK: 'auto_link_bridge_manual_fallback',
  // Canonical stage-4 bridge-link names, composed via the typed builder above.
  // canonical-event-map.json records each pair's retirement state. BRIDGE_LINK_FAILED
  // merges two historical names; `failure_kind` keeps the merged stream separable.
  // BRIDGE_LINK_EMPTY is S3/warn — "no devices found yet" is an EXPECTED outcome,
  // never a failure, and must stay out of blocking-spike alerts (see
  // EVENT_SEVERITY in event-properties.ts).
  BRIDGE_LINK_ATTEMPT: eventName(EVENT_ROOT.BRIDGE_LINK, EVENT_STATUS.ATTEMPT),
  BRIDGE_LINK_SUCCEEDED: eventName(EVENT_ROOT.BRIDGE_LINK, EVENT_STATUS.SUCCEEDED),
  BRIDGE_LINK_FAILED: eventName(EVENT_ROOT.BRIDGE_LINK, EVENT_STATUS.FAILED),
  BRIDGE_LINK_TIMEOUT: eventName(EVENT_ROOT.BRIDGE_LINK, EVENT_STATUS.TIMEOUT),
  BRIDGE_LINK_EMPTY: eventName(EVENT_ROOT.BRIDGE_LINK, EVENT_STATUS.EMPTY),
  BRIDGE_LINK_RETRY: eventName(EVENT_ROOT.BRIDGE_LINK, EVENT_STATUS.RETRY),
  // ENG-4321 terminal-outcome events for a retry-prone stream-recovery flow.
  // Composed via the typed builder above (not raw literals), so an off-matrix
  // root/status pair fails `tsc`. Net-new canonical events (no rename, no
  // dual-write): they add a terminal success/failure signal where none existed,
  // so no existing consumer or saved PostHog insight is affected.
  STREAM_RECOVERY_SUCCEEDED: eventName(EVENT_ROOT.STREAM_RECOVERY, EVENT_STATUS.SUCCEEDED),
  STREAM_RECOVERY_FAILED: eventName(EVENT_ROOT.STREAM_RECOVERY, EVENT_STATUS.FAILED),
  USER_UPDATE_EMAIL: 'update_email',
  USER_UPDATE_NAME: 'update_name',
  USER_UPLOAD_PROFILE_PHOTO: 'upload_profile_photo',
  WAKE_UP_SOURCE: 'wake_up_source',
  ZOOM_OUT_JOYSTICK: 'zoom_out_joystick',

  // Multi-Add Phase 2 catalog — analytics foundation per PRD §12. P1
  // shipped the first 5 names as catalog stubs; P2 promotes them to
  // active emit sites via `emitMultiAddAnalytics` and adds the
  // remaining 6 names that close the lifecycle loop (modal open/cancel,
  // single-add baseline, notification follow-up). Per
  // `code-comments-required` (ai.knowledge/global): emit sites live in
  // `analytics/utils/emit-multi-add-analytics.ts`, not here.
  /** User initiates a bulk add from the discovery modal (N≥2 selected) */
  MULTI_ADD_STARTED: 'multi_add_started',
  /** runBulkCommit finished — emits with full outcome counts + duration */
  MULTI_ADD_COMPLETED: 'multi_add_completed',
  /** Co-emits with `completed` when ≥1 device is non-fully-configured */
  MULTI_ADD_PARTIAL_SUCCESS: 'multi_add_partial_success',
  /** Per-device commit succeeded — carries link_outcome / probe_outcome property */
  MULTI_ADD_DEVICE_ADDED: 'multi_add_device_added',
  /** Per-device commit produced a chip/skip — carries skip_reason */
  MULTI_ADD_DEVICE_NEEDS_ATTENTION: 'multi_add_device_needs_attention',
  /**
   * User clicked an on-tile status chip (Multi-Add Phase 2, PRD §2.3/§2.4)
   * — e.g. the "Authenticate" / needs-attention chip that deep-links into
   * Advanced Settings. Carries the chip `variant` so the funnel can split
   * authenticate (needs-attention) vs added-with-warnings vs failed
   * recovery clicks. Emitted from `room/components/left-tray/chip.tsx`.
   */
  MULTI_ADD_CHIP_CLICKED: 'multi_add_chip_clicked',
  /**
   * User toggled a discovery-table multi-select checkbox (Multi-Add Phase 2,
   * PRD §2.1). Carries the toggle direction (`select` / `deselect`) and the
   * resulting selected count. Emitted from
   * `device/discovery/components/selector/table.tsx`.
   */
  MULTI_ADD_DISCOVERY_SELECTED: 'multi_add_discovery_selected',
  /** User clicked a row in the masthead notification panel (W2) */
  MULTI_ADD_NOTIFICATION_CLICKED: 'multi_add_notification_clicked',
  /** Device transitioned to fully-configured via the notification deep-link (W5) */
  DEVICE_CONFIGURATION_COMPLETED_FROM_NOTIFICATION:
    'device_configuration_completed_from_notification',
  /** Single-add modal commit started — analytics baseline for multi-vs-single comparison */
  SINGLE_ADD_STARTED: 'single_add_started',
  /** Single-add modal commit resolved (success or failure) */
  SINGLE_ADD_COMPLETED: 'single_add_completed',
  /** Add-source modal opened (any flow — discovery, manual, sidebar +) */
  ADD_SOURCE_MODAL_OPENED: 'add_source_modal_opened',
  /** Add-source modal closed without a commit */
  ADD_SOURCE_MODAL_CANCELLED: 'add_source_modal_cancelled',
  // Multi-Add Phase 2 (PRD §2.10) — explicit batch cancellation.
  // Distinct from MULTI_ADD_COMPLETED: emitted when the user clicks
  // Cancel on the bulk-progress overlay mid-batch, with partial counts
  // captured at the break point. Per `reference-rules-in-comments`
  // (ai.knowledge/global).
  /** User explicitly cancelled an in-flight bulk add — partial counts only */
  MULTI_ADD_BATCH_CANCELLED: 'multi_add_batch_cancelled',
  // Multi-Add Phase 2 — per-tab "needs attention" warning surfaced.
  // Emitted by the `useNeedAttentionTabs` hook (`use-need-attention-tabs.ts`)
  // on each `false → true` transition of a sub-tab's boolean, deduped
  // via the depot's `shownSubtabAnalytics` slice so one transition
  // emits exactly one event per session. Per
  // `reference-rules-in-comments` (ai.knowledge/global).
  /** Per-(device, tab) needs-attention warning icon first surfaced in this session */
  MULTI_ADD_SUBTAB_NEEDS_ATTENTION_SHOWN: 'multi_add_subtab_needs_attention_shown',
  // ENG-4486 — fire-side post-add alert analytics. Emitted by the
  // `useMultiAddBucketNotifications` observer's declarative reconciliation
  // whenever a device's DESIRED alert changes to a new one, carrying the PRD
  // §13 `chip_type` (authentication / configure_video / configure_control /
  // select_model / multiple). Because the reconciliation only posts on a
  // genuine desired-identity change (never every tick) and each identity maps
  // to a distinct chip_type, this fires once per transition without a separate
  // dedup slice. A DEDICATED event — kept separate from the add-commit
  // `MULTI_ADD_DEVICE_NEEDS_ATTENTION` funnel so the two are not conflated.
  // Per `reference-rules-in-comments` (ai.knowledge/global).
  /** Post-add "needs configuration" alert first surfaced for a device (carries chip_type) */
  MULTI_ADD_ALERT_SHOWN: 'multi_add_alert_shown',
  // ─── In-studio screen views (ENG-4324, plan SCR-1) ───
  // Named screen_* canonical events give each in-studio (room) screen its own
  // event so Product can see WHICH room a user is on — the generic $pageview
  // collapsed every /room screen into one. These name STRINGS live here (the
  // canonical-name home, alongside every other event name); the screen -> name
  // MAP that open decision #7 requires as a single source of truth is
  // IN_STUDIO_SCREEN_EVENTS in analytics/screens/screen-events.ts, which
  // references these constants (not re-typed literals) so the map and the names
  // can never drift. The generic $pageview is NOT retired by this ticket, so
  // there is no dual_write on these names.
  //
  // Deliberately its own section at the END of the map rather than beside the
  // boot names it reads next to: this object is edited by a dozen concurrent
  // branches, and every insertion that shares an anchor with another branch
  // produces an add/add conflict whose one-side-wins resolution silently drops
  // the other branch's constants. The map has no enforced ordering, so an
  // uncontested anchor costs nothing. Per rule: reference-rules-in-comments
  // (ai.knowledge/global).
  SCREEN_ROOM: 'screen_room',
  SCREEN_ROOM_BOOT: 'screen_room_boot',
  SCREEN_ROOM_INSPECT: 'screen_room_inspect',
  SCREEN_ROOM_BILLING: 'screen_room_billing',
  // ENG-4874 alert and sign-out telemetry. Plain literals, not eventName():
  // the ENG-4340 matrix sanctions stage-outcome tokens, and an alert's three
  // moments (shown / closed / acted) are not outcomes of a stage. Own section
  // at the end, like the screen names above, so concurrent branches do not
  // share an insertion anchor. Per rule: reference-rules-in-comments
  // (ai.knowledge/global); contract: ENG-4874 "Event contract (Oct 28)".
  ALERT_SHOWN: 'alert_shown',
  ALERT_CLOSED: 'alert_closed',
  ALERT_ACTED: 'alert_acted',
  SIGN_OUT: 'sign_out',
  SIGN_IN: 'sign_in',
  // ENG-4873 (N2): sent ONLY when the single web retry of a failed PostHog start
  // succeeds, carrying the incident_id of the console line written at the time of
  // the failure. Desktop records the failure in the main-process session log and
  // never sends this event. Own section (see the screen_* note above).
  ANALYTICS_START_FAILED: 'analytics_start_failed'
} as const;

export type EventName = (typeof EVENT_NAMES)[keyof typeof EVENT_NAMES];

// ENG-4348 stage 3 dual-emit pair, canonical name first. Every auth-failure
// emission site iterates this tuple instead of naming events inline, so the
// eventual legacy cutover is a one-line change here rather than a hunt across
// call sites. Per rule reference-rules-in-comments (ai.knowledge/global) and
// the ENG-4340 canonical map: legacy `oauth_error` sunsets after the >=30-day
// parity window recorded in canonical-event-map.json.
export const AUTH_LOGIN_FAILURE_EVENT_NAMES = [
  EVENT_NAMES.AUTH_LOGIN_FAILED,
  EVENT_NAMES.OAUTH_ERROR
] as const;

export type AuthLoginFailureEventName = (typeof AUTH_LOGIN_FAILURE_EVENT_NAMES)[number];

/**
 * Legacy constants retained for active migration windows and historical readers.
 * Distinct export per the ENG-4340
 * contract's dual_emit_rule: call sites address the legacy leg of a dual-emit
 * through THIS map, so a retirement removes one tuple entry instead of hunting
 * call sites. Historical EVENT_NAMES values remain available for absence checks.
 */
export const LEGACY_EVENT_NAMES = {
  DEVICE_DISCOVERY_DEVICE_FOUND: EVENT_NAMES.DEVICE_DISCOVERY_DEVICE_FOUND,
  DEVICE_DISCOVERY_NO_DEVICES_FOUND: EVENT_NAMES.DEVICE_DISCOVERY_NO_DEVICES_FOUND,
  // ENG-4311 historical names remain addressable after their senders retire.
  AUTO_LINK_BRIDGE_ATTEMPT: EVENT_NAMES.AUTO_LINK_BRIDGE_ATTEMPT,
  AUTO_LINK_BRIDGE_SUCCESS: EVENT_NAMES.AUTO_LINK_BRIDGE_SUCCESS,
  AUTO_LINK_BRIDGE_FAILURE: EVENT_NAMES.AUTO_LINK_BRIDGE_FAILURE,
  AUTO_LINK_BRIDGE_ERROR: EVENT_NAMES.AUTO_LINK_BRIDGE_ERROR,
  AUTO_LINK_BRIDGE_RETRY: EVENT_NAMES.AUTO_LINK_BRIDGE_RETRY,
  AUTO_LINK_BRIDGE_TIMEOUT: EVENT_NAMES.AUTO_LINK_BRIDGE_TIMEOUT,
  AUTO_LINK_BRIDGE_NO_DEVICES: EVENT_NAMES.AUTO_LINK_BRIDGE_NO_DEVICES,
  BRIDGE_GOES_OFFLINE: EVENT_NAMES.BRIDGE_GOES_OFFLINE
} as const;

export type LegacyEventName = (typeof LEGACY_EVENT_NAMES)[keyof typeof LEGACY_EVENT_NAMES];

/**
 * Bridge-link migration names. Every retired outcome now keeps only the canonical
 * sender. Historical constants remain available to compatibility readers above.
 */
export const BRIDGE_LINK_EVENT_NAMES = {
  ATTEMPT: [EVENT_NAMES.BRIDGE_LINK_ATTEMPT],
  SUCCEEDED: [EVENT_NAMES.BRIDGE_LINK_SUCCEEDED],
  TIMEOUT: [EVENT_NAMES.BRIDGE_LINK_TIMEOUT],
  EMPTY: [EVENT_NAMES.BRIDGE_LINK_EMPTY],
  // Per rule: think-in-systems (ai.knowledge/global) — one retirement point.
  RETRY: [EVENT_NAMES.BRIDGE_LINK_RETRY],
  CONNECTION_UNAVAILABLE: [EVENT_NAMES.BRIDGE_CONNECTION_UNAVAILABLE]
} as const;

/**
 * The merge maps two historical names to `bridge_link_failed`. Each call site uses
 * the tuple for its leg, which keeps each historical meaning explicit after both senders retire.
 */
export const BRIDGE_LINK_FAILED_EVENT_NAMES = {
  LINK_FAILURE: [EVENT_NAMES.BRIDGE_LINK_FAILED],
  LINK_ERROR: [EVENT_NAMES.BRIDGE_LINK_FAILED]
} as const;

/**
 * Discriminator carried on every `bridge_link_failed` so the two-into-one merge
 * loses no information: `link_failure` is a handled, expected-shape link failure
 * (the old `auto_link_bridge_failure`), `link_error` is an unexpected thrown
 * error caught by the controller (the old `auto_link_bridge_error`). Consumers
 * that used to split on event name split on this property instead — which is why
 * canonical-event-map.json lists it as a required_property.
 */
export const BRIDGE_LINK_FAILURE_KIND = {
  LINK_FAILURE: 'link_failure',
  LINK_ERROR: 'link_error'
} as const;

export type BridgeLinkFailureKind =
  (typeof BRIDGE_LINK_FAILURE_KIND)[keyof typeof BRIDGE_LINK_FAILURE_KIND];
